import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, HelpCircle } from 'lucide-react';

const OnboardingTour = ({ steps, onComplete, onSkip, isOpen, onStepChange }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [highlightStyle, setHighlightStyle] = useState({});
    const tourRef = useRef(null);

    useEffect(() => {
        if (isOpen && steps[currentStep]?.target) {
            updateHighlight();
            window.addEventListener('resize', updateHighlight);
            window.addEventListener('scroll', updateHighlight);
        }
        return () => {
            window.removeEventListener('resize', updateHighlight);
            window.removeEventListener('scroll', updateHighlight);
        };
    }, [isOpen, currentStep, steps]);

    // Notify parent of step changes
    useEffect(() => {
        if (isOpen && onStepChange) {
            onStepChange(currentStep);
        }
    }, [isOpen, currentStep, onStepChange]);

    const updateHighlight = () => {
        const target = document.querySelector(steps[currentStep].target);
        if (target) {
            const rect = target.getBoundingClientRect();
            const padding = 8;
            setHighlightStyle({
                top: rect.top - padding + window.scrollY,
                left: rect.left - padding + window.scrollX,
                width: rect.width + padding * 2,
                height: rect.height + padding * 2,
                opacity: 1,
            });

            // Simple scroll into view if needed
            if (rect.top < 0 || rect.bottom > window.innerHeight) {
                target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        } else {
            setHighlightStyle({ opacity: 0 });
        }
    };

    // Reset to step 0 when tour closes
    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(0);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const step = steps[currentStep];

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            onComplete();
        }
    };

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] pointer-events-none overflow-hidden font-sans">
            {/* Dimmed background with "hole" */}
            <div
                className="absolute inset-0 bg-black/60 transition-all duration-300"
                style={{
                    clipPath: highlightStyle.width
                        ? `polygon(0% 0%, 0% 100%, ${highlightStyle.left}px 100%, ${highlightStyle.left}px ${highlightStyle.top}px, ${highlightStyle.left + highlightStyle.width}px ${highlightStyle.top}px, ${highlightStyle.left + highlightStyle.width}px ${highlightStyle.top + highlightStyle.height}px, ${highlightStyle.left}px ${highlightStyle.top + highlightStyle.height}px, ${highlightStyle.left}px 100%, 100% 100%, 100% 0%)`
                        : 'none',
                    pointerEvents: 'auto'
                }}
                onClick={onSkip}
            />

            {/* Floating Card */}
            <div
                className="absolute transition-all duration-300 pointer-events-auto w-[280px] sm:w-[320px]"
                style={{
                    // Smart positioning: place above if element is in lower 60% of viewport
                    // Otherwise place below. Never go above 80px from top or below viewport - 220px.
                    top: (() => {
                        const tooltipHeight = 200;
                        const elementBottom = highlightStyle.top + highlightStyle.height;
                        const isInLowerHalf = highlightStyle.top > window.innerHeight * 0.4;
                        const maxTop = window.innerHeight - 220; // Leave room for tooltip + buttons

                        let calculatedTop;
                        if (isInLowerHalf) {
                            // Position above the element
                            calculatedTop = highlightStyle.top - tooltipHeight - 20;
                        } else {
                            // Position below the element
                            calculatedTop = elementBottom + 20;
                        }

                        // Clamp between 80px and maxTop
                        return Math.min(Math.max(80, calculatedTop), maxTop);
                    })(),
                    left: Math.min(Math.max(20, highlightStyle.left), window.innerWidth - 340),
                }}
            >
                <div className="bg-slate-900 border border-cyan-500/50 rounded-2xl p-5 shadow-2xl shadow-cyan-500/20 text-white">
                    <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2 text-cyan-400">
                            <HelpCircle className="w-5 h-5" />
                            <span className="text-xs font-bold uppercase tracking-wider">Paso {currentStep + 1} de {steps.length}</span>
                        </div>
                        <button onClick={onSkip} className="text-slate-400 hover:text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                    <p className="text-slate-300 text-sm leading-relaxed mb-6">
                        {step.content}
                    </p>

                    <div className="flex justify-between items-center">
                        <button
                            onClick={onSkip}
                            className="text-slate-400 text-sm hover:text-slate-200"
                        >
                            Saltar
                        </button>
                        <div className="flex gap-2">
                            {currentStep > 0 && (
                                <button
                                    onClick={handlePrev}
                                    className="p-2 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 transition-colors"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                            )}
                            <button
                                onClick={handleNext}
                                className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-bold py-2 px-4 rounded-xl transition-all shadow-lg shadow-cyan-500/30"
                            >
                                {currentStep === steps.length - 1 ? '¡Entendido!' : 'Siguiente'}
                                {currentStep < steps.length - 1 && <ChevronRight className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Pointer Arrow */}
                <div
                    className="absolute left-1/2 -ml-2 w-4 h-4 bg-slate-900 border-l border-t border-cyan-500/50 rotate-45"
                    style={{
                        top: highlightStyle.top + highlightStyle.height + 20 > window.innerHeight - 200 ? 'auto' : '-8px',
                        bottom: highlightStyle.top + highlightStyle.height + 20 > window.innerHeight - 200 ? '-8px' : 'auto',
                        display: highlightStyle.width ? 'block' : 'none'
                    }}
                />
            </div>
        </div>
    );
};

export default OnboardingTour;
